import { useContext, useEffect } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetRouteInfoQuery } from 'state/apiSlice';
import {
  selectScenarioRunUUID,
  selectScenarioSegments,
  setScenarioCapacity,
  setScenarioDaysOfWeek,
  setScenarioEnd,
  setScenarioLength,
  setScenarioMaxDetours,
  setScenarioName,
  setScenarioRunUUID,
  setScenarioSegments,
  setScenarioStart,
  useGetScenarioQuery,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { isFetchBaseQueryErrorType } from 'state/store';
import {
  selectCurrentScenarioId,
  selectProject,
  setRouteSelection,
  setSelectedSegmentId,
  setTypicalMonth,
} from 'state/workflowSlice';

import { HTTP_STATUS } from 'appConstants';

export function useScenarioSettingsData() {
  const project_slug = useAppSelector(selectProject);
  const dispatch = useAppDispatch();
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const scenarioRunUUID = useAppSelector(selectScenarioRunUUID);

  const {
    currentData: scenarioData,
    error,
    isLoading,
    refetch,
    isUninitialized,
  } = useGetScenarioQuery(
    { project_slug, scenario_id: currentScenarioId },
    {
      skip: !currentScenarioId,
    },
  );
  const notFoundError =
    error &&
    isFetchBaseQueryErrorType(error) &&
    error.status === HTTP_STATUS.NOT_FOUND;
  const scenarioSegments = useAppSelector(selectScenarioSegments);

  useEffect(() => {
    if (scenarioRunUUID && !isUninitialized) refetch();
  }, [scenarioRunUUID, isUninitialized]);

  useEffect(() => {
    if (scenarioData) {
      dispatch(setScenarioDaysOfWeek(scenarioData.scenario.days_of_week));
      dispatch(setScenarioStart(scenarioData.scenario.closure_start));
      dispatch(setScenarioEnd(scenarioData.scenario.closure_end));
      dispatch(setTypicalMonth(scenarioData.scenario.year_month));
      dispatch(setScenarioSegments(scenarioData.scenario.segments));
      dispatch(
        setScenarioCapacity(scenarioData.scenario.new_capacities[0] || 0.0),
      );
      dispatch(
        setScenarioMaxDetours(scenarioData.scenario.max_detours_per_hour),
      );
      dispatch(setScenarioName(scenarioData.scenario.scenario_name));
      dispatch(setScenarioRunUUID(scenarioData.scenario.uuid));
      dispatch(setScenarioLength(scenarioData.scenario.length));
    }
  }, [scenarioData]);

  useEffect(() => {
    if (currentScenarioId === undefined) {
      if (scenarioSegments.length > 0) {
        dispatch(setSelectedSegmentId(scenarioSegments[0]));
        dispatch(setRouteSelection(scenarioSegments));
      }
      dispatch(setScenarioSegments([]));
    } else if (!scenarioData) {
      dispatch(setScenarioSegments([]));
    }
  }, [scenarioRunUUID, currentScenarioId]);

  return {
    scenarioData,
    notFoundError,
    isLoading,
  };
}

export function useSelectedScenarioClosureRoute() {
  const { layer } = useContext(LayerContext);
  const project = useAppSelector(selectProject);
  const scenarioSegments = useAppSelector(selectScenarioSegments);
  const routeInfo = useGetRouteInfoQuery(
    { project_slug: project, segment_ids: scenarioSegments, layer_id: layer },
    {
      skip:
        !scenarioSegments ||
        scenarioSegments.length === 0 ||
        !project ||
        !layer,
    },
  );
  return routeInfo;
}
